import { type ReactNode } from 'react';
import { radius } from '@karehero/llama/theme/radius';

import { Box, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import Note from '@/designs-dev/annotations/Note';
import { type Screen } from '@/designs-dev/loft/projects';
import { ScreenSelectContext } from '@/designs-dev/loft/ScreenSelectContext';

// The chrome around a prototype: which take on it you are looking at, above, and
// what is different about this one, below.
//
// Two prototypes can open on the same screen — the Keni and Benefits journeys
// both start on "Hi, I'm Keni" — so from the menu they were indistinguishable
// and telling them apart meant clicking into each and walking it. The switch
// puts them side by side and the note says what each one is for.
//
// The same pill and the same select as the state selector beside the drill
// toggle, because it is the same kind of control: which of these am I looking
// at. Chrome around the design, not part of it — and in the same place every
// other stage puts its controls, a pill row directly above the frame. It used to
// be pinned to the top of the page, which put it nowhere near either the design
// it acts on or the row a CAM already looks in for drill mode and the state.
//
// Labelled Exploration, not Version. A version is v1 against v2 of the same
// idea, which the URL scheme already owns and which these are not: the whole
// journey and the benefits-only cut are two different questions being asked at
// once, and neither supersedes the other. The word sits in front of the select
// the way STAGE does on the frame below it — said once, rather than prefixed
// onto both options.
const ScreenChrome = ({
  screen,
  siblings,
  pathOf,
  children,
}: {
  screen: Screen;
  /** Every take on this screen, this one included. */
  siblings: Screen[];
  pathOf: (s: Screen) => string;
  children?: ReactNode;
}) => {
  const navigate = useNavigate();
  const hasSwitch = siblings.length > 1;

  const select = hasSwitch && (
    <Stack
      sx={{
        p: '4px',
        alignItems: 'center',
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'custom.neutral.200',
        borderRadius: `${radius.md}px`,
      }}
      direction='row'
    >
      {/* User, not Exploration. The switch was built for competing takes on one
          idea; where it carries the sides of one flow, what it is picking is
          whose view of it you are looking at. */}
      <Typography sx={{ pl: 1 }} variant='overline' color='text.secondary'>
        User
      </Typography>
      <TextField
        slotProps={{ input: { disableUnderline: true } }}
        select
        size='small'
        variant='standard'
        value={screen.slug}
        onChange={(e) => {
          const next = siblings.find((s) => s.slug === e.target.value);
          if (next) navigate(pathOf(next));
        }}
        aria-label='User'
        sx={{ minWidth: 180, px: 1 }}
      >
        {siblings.map((s) => (
          <MenuItem key={s.slug} value={s.slug}>
            {s.variantLabel ?? s.label}
          </MenuItem>
        ))}
      </TextField>
    </Stack>
  );

  // A screen that lays out its own stage keeps the window: centring it at its
  // natural size turned the page colour it paints into a grey box behind the
  // design. So nothing is wrapped around it, and the select is handed to the
  // stage to put in the row it already draws.
  if (screen.fills) {
    return (
      <ScreenSelectContext.Provider value={select}>
        {children}
      </ScreenSelectContext.Provider>
    );
  }

  return (
    // Pill row, prototype and note as one centred group. One group is what keeps
    // them together: giving the prototype the whole remaining height and hanging
    // the switch off the top and the note off the bottom of the page put a
    // screenful of nothing between a screen and the two things talking about it.
    // Centred rather than left, because a prototype that lays itself out bare
    // sat against the left edge. Scrolls as a group, for a stage taller than the
    // window.
    <Stack
      sx={{
        height: '100%',
        minHeight: 0,
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        overflow: 'auto',
      }}
      spacing={2}
    >
      {select}

      {children}

      {/* The note, under the frame rather than over it, and in an annotation
          rather than as loose grey text.
          Both for the same reason. Above the prototype it was the first thing
          read on the way to the thing you came for, and set as a centred
          paragraph it read as part of the design rather than as somebody talking
          about it. Underneath, in a bordered note with a title bar on it, it is
          plainly a remark about the screen you have just looked at. */}
      {screen.note && (
        <Box sx={{ width: '100%', maxWidth: 640, flexShrink: 0 }}>
          <Note>{screen.note}</Note>
        </Box>
      )}
    </Stack>
  );
};

export default ScreenChrome;
